import Logger from "../Logger";
import Point from "../models/Point";
import WatchableValue from "../models/WatchableValue";
import { sleep } from "../models/utils";
import { Guide } from "./Guide";
import { ToDoItem } from "./ToDoItem";
import storyLines_Framework from "./storylines/Framework";
import StoryLine_BaseIntroduction from "./storylines/BaseIntroduction";
import StoryLine_CabinetPlanning from "./storylines/CabinetPlanning";
import StoryLine_ScenesAndMultiplexer from "./storylines/SenesAndMultilpexers";
import StoryLine from "./storylines/StoryLine";

const logger = new Logger();

export type GuideExplainData = {
  text: any;
  elm?: HTMLElement | null | undefined;
  done: () => void;
};

export type SyncInfo = {
  id: string;
  info?: string;
};

export default class GuideMode {
  // currentMode = new WatchableValue<GuideState>(GuideState.hidden);

  syncPointReached = new WatchableValue<SyncInfo | undefined>(undefined);
  guide: Guide | undefined;

  get isHidden(): boolean {
    //
    // always true if we are not inside app
    //
    if (!state.loggedInUser) {
      return true;
    }
    if (!window.location.href.toLocaleLowerCase().endsWith("/app")) {
      return true;
    }

    const se = localStorage.getItem(`guideIsHidden`) || "true";
    return se == "true";
  }

  set isHidden(newValue: boolean) {
    if (this.isHidden != newValue) {
      localStorage.setItem(`guideIsHidden`, newValue ? "true" : "false");
      this.updateUi();
    }
  }

  //
  // Hier warten wir bis eine lang laufende Function bis diese
  //
  //  "state.guide.raiseSync()" aufruft
  //

  async waitForSync(waitId: string | undefined): Promise<SyncInfo> {
    return new Promise((resolve) => {
      let handle: any | undefined;

      const self = this;

      function h() {
        if (self.syncPointReached.value) {
          self.syncPointReached.onChange.unsubscribe(handle);
          const res = self.syncPointReached.value;
          self.syncPointReached.value = undefined;
          if (!waitId || waitId == res.id) {
            resolve(res);
          }
        }
      }

      handle = this.syncPointReached.onChange.subscribe(h);
    });
  }

  raiseSync(syncInfo: SyncInfo) {
    state.guideMode.syncPointReached.value = syncInfo;
  }

  clearSync() {
    state.guideMode.syncPointReached.value = undefined;
  }

  onChangeOnChangeHandler: any = 0;

  // modeText = new WatchableValue<string | any>(undefined);
  // toDoItem = new WatchableValue<ToDoItem | undefined>(undefined);

  updateUiTrigger = new WatchableValue<number>(0);
  updateUi() {
    this.updateUiTrigger.raiseOnChange();
  }

  clear() {
    if (this.guide) {
      this.guide.cancel = true;
    }
    this.guide = undefined;
    this.explain = undefined;
    this.sequenceRunning = false;
    this._currentStepStack = [];
    this._prefixStepDone = false;
    this.syncPointReached.value = undefined;
    this.updateUi();
  }

  // nextMode = GuideState.unknown;

  explain: GuideExplainData | undefined;

  sequenceRunning = false;

  get currentText(): string {
    return this.currentStep?.text || "";
  }

  // constructor() {
  //   this.onChangeOnChangeHandler = globalOnChange.subscribe(
  //     (changedObject: BaseObject, changedProperty: string | any | undefined) => {
  //       if (changedObject != this.updateUiTrigger) {
  //         this.determineCurrentMode();
  //       }
  //     },
  //   );

  // return () => {
  //   globalOnChange.unsubscribe(globalOnChangeOnChangeHandler.current);
  //   globalOnChangeOnChangeHandler.current = 0;
  // };
  //}

  async runSequence(ev: any, todoItem: ToDoItem) {
    const sequence = todoItem.getPattern();

    logger.log(`runSequence ${todoItem.id}`);

    const guide = new Guide(sequence);

    function onKeydown(ev: any) {
      ev = ev || window.event; // Event object 'ev'
      var key = ev.which || ev.keyCode; // Detecting keyCode
      if (key == 27) {
        guide.cancel = true;
      }
    }

    /////////////////////////////////////////////

    const startPoint = new Point(ev.clientX, ev.clientY);

    this.sequenceRunning = true;
    document.body.addEventListener("keydown", onKeydown);

    try {
      //
      // run the sequence of this step, annd await the end of this sequence
      //
      await sleep(100);
      this.guide = guide;
      await guide.go(startPoint);
    } finally {
      this.guide = undefined;
      document.body.removeEventListener("keydown", onKeydown);
      this.sequenceRunning = false;
    }

    //
    // Sequence ended
    //

    if (this.currentStep) {
      this.currentStep.executed = true;
    }

    //
    // gehe zum nächten Step
    //
    // this.forewardStep();

    // this.toDoItem.value = todoItem.nextStep ? todoItem.nextStep() : undefined;
    this.guide = undefined;

    logger.log(`runSequence done ${todoItem.id} currentStep=${this.currentStep?.id}`);
  }

  getPossibleToDoElements(): Array<{ thisElm: ToDoItem; lastElm: ToDoItem | undefined }> {
    let all = new Array<{ thisElm: ToDoItem; lastElm: ToDoItem | undefined }>();

    function getAllPossiblToDoElements(toDoElm: ToDoItem, lastToDoElm?: ToDoItem) {
      if (toDoElm.done(lastToDoElm)) {
        /// if ToDo Element done get inside of the next steps of ToDo Element und get there all possible ToDo Elements
        toDoElm.subSteps?.forEach((step) => {
          getAllPossiblToDoElements(step(), toDoElm);
        });
      } else if (toDoElm.pattern) {
        let putIn = true;
        //
        // check here if the toDoElm is allready in Array of all Possible ToDoElemt
        //
        all.forEach((elm) => {
          if (elm.thisElm.prio == toDoElm.prio && elm.lastElm?.prio == lastToDoElm?.prio) {
            putIn = false;
          }
        });
        if (putIn) {
          all.push({ thisElm: toDoElm, lastElm: lastToDoElm });
        }
      }
    }
    /// get here all Possible ToDoElms
    ToDoItem.firstInHierarchie.forEach((toDoElm: ToDoItem) => {
      getAllPossiblToDoElements(toDoElm);
    });
    /// give only the 3 ToDoElements with the highest Priority back
    const res = new Array<any>();
    all
      .sort((a, b) => {
        return b.thisElm.prio - a.thisElm.prio;
      })
      .forEach((elm) => {
        if (res.length <= 2) {
          res.push(elm);
        }
      });
    return res;
  }

  _currentStepStack = new Array<ToDoItem | undefined>();
  _prefixStepDone = false;

  // currentStep is readonly
  //
  get currentStep(): ToDoItem | undefined {
    if (this._currentStepStack.length > 0) {
      return this._currentStepStack[this._currentStepStack.length - 1];
    }

    return undefined;
  }

  get currentStepDone(): boolean {
    for (let s of this._currentStepStack) {
      if (s && s.done()) return true;
    }
    return false;
  }

  forewardStep() {
    console.log("forewardStep A ", this.currentStep?.id);
    if (this._currentStepStack.length > 0) {
      const ps = this._currentStepStack.pop();

      const n = ps && ps.nextStep && ps.nextStep();
      console.log("forewardStep B ", n?.id, this._currentStepStack.length);

      if (n) {
        this._currentStepStack.push(n);
        if (n && n.prefixStep && n.prefixStep()) {
          this._currentStepStack.push(n.prefixStep());
        }
      }
    }
  }

  allStoryLines = new Array<StoryLine>(
    StoryLine_BaseIntroduction,
    StoryLine_ScenesAndMultiplexer,
    StoryLine_CabinetPlanning,
  );

  startTimer: any = 0;

  get currentStorylineIndex(): number {
    return parseInt(localStorage.getItem("currentStorylineIndex") || "0");
  }

  set currentStorylineIndex(newValue: number) {
    localStorage.setItem("currentStorylineIndex", `${newValue}`);
  }

  onProjectOpened() {
    if (!this.isHidden) {
      const index = this.currentStorylineIndex;
      if (index >= 0) {
        if (this.allStoryLines[this.currentStorylineIndex].allStepsAreDone()) {
          this.onReopenFromMenu();
        } else {
          this.startCurrentStoryline(true);
        }
      }
    }
  }

  onReopenFromMenu() {
    state.guideMode.isHidden = false;
    state.guideMode.setStartStep(storyLines_Framework.select_A());
  }

  startCurrentStoryline(withIntro: boolean) {
    if (this.currentStorylineIndex < 0) {
      //
      // currentGuideModeIndex == -1 => no story line is currently active
      //
    } else {
      const firstItem = this.allStoryLines[this.currentStorylineIndex].firstItem;
      const xToDoItem = firstItem ? firstItem() : undefined;
      if (xToDoItem) {
        if (withIntro) {
          //
          // put introduce in front of the sequence
          // and attach the first step of the stopy line to the intro step
          //
          const intro = storyLines_Framework.introStep();
          intro.nextStep = () => {
            return xToDoItem;
          };
          this.setStartStep(intro);
        } else {
          this.setStartStep(xToDoItem);
        }

        this.startTimer = 0;
      }
    }
  }

  setStartStep(step: ToDoItem | undefined) {
    if (step) {
      this._currentStepStack = [step];
      if (step && step.prefixStep && step.prefixStep()) {
        this._currentStepStack.push(step.prefixStep());
      }

      this.forwardDoneSteps();
    } else {
      this._currentStepStack = [];
    }
    this.updateUi();
  }

  forwardDoneSteps() {
    while (this.currentStep && (this.currentStepDone || this.currentStep?.pattern == undefined)) {
      console.log("forwardDoneSteps ", this.currentStep.id);
      this.forewardStep();
    }
    this.updateUi();
  }

  // determineCurrentMode() {
  //   if (this.sequenceRunning) {
  //     return;
  //   }

  //   if (this.currentStep) {
  //     this.startCurrentStoryline();
  //   } else {
  //     console.log("determineCurrentMode A ", this.currentStep.id);

  //     this.forwardDoneSteps();

  //     console.log("determineCurrentMode B", this.currentStep?.id);

  //     this.updateUi();
  //   }
  // }
}
